import { type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import { JobCardsContainer } from '@/features/jobs/details/components/JobCards/JobCardsContainer'
import { ContentError } from '@/components/ContentError'
import { AccessDeniedView } from '@/components/AccessDeniedView'
import { useActivation } from '@/hooks/view-model-hooks'
import type { JobPaneLoaderData } from '@/features/jobs/job-route-loaders'

export interface JobPaneContainerProps {
  data: JobPaneLoaderData
  empty: ReactNode
  isJobListSidebarVisible: boolean
  onJobListSidebarToggle?: () => unknown
  onPreviousJob?: () => unknown
  onNextJob?: () => unknown
}

/**
 * Renders a job's cards in a pane hosted by another feature's page. The
 * page's route loader loads the job with `createJobPaneLoader`.
 */
export const JobPaneContainer = observer(function JobPaneContainer({
  data,
  empty,
  isJobListSidebarVisible,
  onJobListSidebarToggle,
  onPreviousJob,
  onNextJob,
}: JobPaneContainerProps) {
  const { internal, shellInfo } = useFeatures(({ jobs, shell }) => ({
    internal: jobs.internal(),
    shellInfo: shell.shellInfo,
  }))
  useActivation(internal.jobRootViewModel)

  if (!data) {
    return empty
  }

  if ('denied' in data) {
    return (
      <AccessDeniedView
        corporateMode={shellInfo.corporateMode}
        title={data.denied}
      />
    )
  }

  if ('error' in data) {
    return <ContentError error={data.error} />
  }

  return (
    <JobCardsContainer
      vm={data.vm}
      onJobListToggleHoverChange={noop}
      isJobListSidebarVisible={isJobListSidebarVisible}
      onJobListSidebarToggle={onJobListSidebarToggle}
      onPreviousJob={onPreviousJob}
      onNextJob={onNextJob}
    />
  )
})

const noop = () => undefined
